import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { SLIDES } from "../../data";
import { Reveal } from "./Reveal";

export const Slider = () => {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const count = SLIDES.length;

  const paginate = useCallback(
    (d) => {
      setDir(d);
      setIndex((prev) => (prev + d + count) % count);
    },
    [count]
  );

  useEffect(() => {
    const t = setInterval(() => paginate(1), 5000);
    return () => clearInterval(t);
  }, [paginate]);

  const slide = SLIDES[index];

  return (
    <section
      id="work"
      data-testid="slider-section"
      className="relative py-24 sm:py-32 bg-[#0a0a0c] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#E879F9] font-bold mb-4">
            02 — Selected Work
          </p>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-6xl leading-[0.95] max-w-3xl">
            Tours, festivals & <span className="text-[#b533ff]">venues.</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-14 relative">
            <div className="relative aspect-[16/10] sm:aspect-[21/9] overflow-hidden rounded-2xl border border-white/10">
              <AnimatePresence custom={dir} mode="popLayout">
                <motion.div
                  key={index}
                  custom={dir}
                  initial={{ opacity: 0, x: dir * 120, scale: 1.05 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: dir * -120, scale: 1.05 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0"
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.15}
                  onDragEnd={(e, info) => {
                    if (info.offset.x < -80) paginate(1);
                    else if (info.offset.x > 80) paginate(-1);
                  }}
                >
                  <img
                    src={slide.img}
                    alt={slide.title}
                    className="w-full h-full object-cover select-none"
                    draggable={false}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                  <div className="absolute inset-0 ring-1 ring-inset ring-[#b533ff]/20" />

                  <div className="absolute bottom-0 left-0 p-6 sm:p-10">
                    <span className="inline-block text-[10px] uppercase tracking-[0.3em] text-[#050505] bg-[#b533ff] px-3 py-1 rounded-full font-bold mb-4">
                      {slide.kind}
                    </span>
                    <h3
                      data-testid="slide-title"
                      className="font-display font-black uppercase text-3xl sm:text-5xl text-white leading-none"
                    >
                      {slide.title}
                    </h3>
                    <p className="mt-3 text-sm sm:text-base text-gray-300 font-light">
                      {slide.role}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Controls */}
            <div className="mt-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {SLIDES.map((_, i) => (
                  <button
                    key={i}
                    data-testid={`slider-dot-${i}`}
                    onClick={() => {
                      setDir(i > index ? 1 : -1);
                      setIndex(i);
                    }}
                    className={`h-[3px] rounded-full transition-all duration-400 ${
                      i === index ? "w-10 bg-[#b533ff]" : "w-5 bg-white/25 hover:bg-white/50"
                    }`}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-3">
                <span className="font-display text-sm text-gray-500">
                  {String(index + 1).padStart(2, "0")}
                  <span className="text-gray-700"> / {String(count).padStart(2, "0")}</span>
                </span>
                <button
                  data-testid="slider-prev"
                  onClick={() => paginate(-1)}
                  className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-white hover:border-[#b533ff] hover:bg-[#b533ff]/10 transition-colors duration-300"
                  aria-label="Previous"
                >
                  <ArrowLeft size={18} />
                </button>
                <button
                  data-testid="slider-next"
                  onClick={() => paginate(1)}
                  className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-white hover:border-[#b533ff] hover:bg-[#b533ff]/10 transition-colors duration-300"
                  aria-label="Next"
                >
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
